import httpService from '../httpService';
import { store } from '../../redux/store';
import {
  StorageUsage,
  StorageDirectory,
  DirectoryWithChildren,
  StorageItem,
  CreateDirectoryRequest,
  UpdateDirectoryRequest,
  MoveDirectoryRequest,
  UpdateItemRequest,
  MoveItemRequest,
  MoveBatchItemsRequest,
  DeleteBatchItemsRequest,
  StorageLimitError,
} from '../../types/storage';

const storageService = {
  /**
   * Fetches the storage usage for the current community.
   */
  getStorageUsage: async (): Promise<StorageUsage> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.get(`/networks/communities/${communityId}/storage/usage`).then(({ data }): StorageUsage => {
      return {
        total: data.total,
        photoAlbums: data.photoAlbums,
        fileStorage: data.fileStorage,
        maxUsage: data.maxUsage,
        updatedAt: new Date(data.updatedAt),
      };
    });
  },

  // Directory Operations

  /**
   * Get the complete directory tree with nested children and items.
   */
  getDirectoryTree: async (): Promise<DirectoryWithChildren[]> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.get(`/networks/communities/${communityId}/storage/directories/tree`).then(({ data }) => data);
  },

  /**
   * Get a specific directory with immediate children and items.
   */
  getDirectory: async (directoryId: number): Promise<DirectoryWithChildren> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.get(`/networks/communities/${communityId}/storage/directories/${directoryId}`).then(({ data }) => data);
  },

  /**
   * Create a new directory.
   */
  createDirectory: async (request: CreateDirectoryRequest): Promise<StorageDirectory> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.post(`/networks/communities/${communityId}/storage/directories`, request).then(({ data }) => data);
  },

  /**
   * Update directory name (rename).
   */
  updateDirectory: async (
    directoryId: number,
    request: UpdateDirectoryRequest
  ): Promise<StorageDirectory> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.put(`/networks/communities/${communityId}/storage/directories/${directoryId}`, request).then(({ data }) => data);
  },

  /**
   * Move directory to a new parent.
   */
  moveDirectory: async (
    directoryId: number,
    request: MoveDirectoryRequest
  ): Promise<StorageDirectory> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService
      .put(`/networks/communities/${communityId}/storage/directories/${directoryId}/move`, request)
      .then(({ data }) => data);
  },

  /**
   * Delete a directory (cascades to subdirectories and files).
   */
  deleteDirectory: async (directoryId: number): Promise<void> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.delete(`/networks/communities/${communityId}/storage/directories/${directoryId}`);
  },

  // Item (File) Operations

  /**
   * Get items, optionally filtered by directory.
   * @param directoryId - undefined for all items, null for root items, number for specific directory
   */
  getItems: async (directoryId?: number | null): Promise<StorageItem[]> => {
    const communityId = store.getState().application.selectedCommunity;
    const params: Record<string, string> = {};
    if (directoryId !== undefined) {
      params.directoryId = directoryId === null ? 'null' : String(directoryId);
    }
    return httpService
      .get(`/networks/communities/${communityId}/storage/items`, { params })
      .then(({ data }) => data);
  },

  /**
   * Upload a single file.
   * Returns the created item or a storage limit error (status 206).
   */
  uploadFile: async (
    file: File,
    directoryId?: number
  ): Promise<StorageItem | StorageLimitError> => {
    const communityId = store.getState().application.selectedCommunity;
    const formData = new FormData();
    formData.append('file', file);
    const url = directoryId !== undefined
      ? `/networks/communities/${communityId}/storage/items?directoryId=${directoryId}`
      : `/networks/communities/${communityId}/storage/items`;

    return httpService
      .post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(({ data, status }) => {
        if (status === 206) {
          return data as StorageLimitError;
        }
        return data as StorageItem;
      });
  },

  /**
   * Upload multiple files.
   * Returns the created items or a storage limit error (status 206).
   */
  uploadFiles: async (
    files: File[],
    directoryId?: number,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<StorageItem[] | StorageLimitError> => {
    const communityId = store.getState().application.selectedCommunity;
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    const url = directoryId !== undefined
      ? `/networks/communities/${communityId}/storage/items/bulk?directoryId=${directoryId}`
      : `/networks/communities/${communityId}/storage/items/bulk`;

    return httpService
      .post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      })
      .then(({ data, status }) => {
        if (status === 206) {
          return data as StorageLimitError;
        }
        return data as StorageItem[];
      });
  },

  /**
   * Update item filename (rename).
   */
  updateItem: async (itemId: number, request: UpdateItemRequest): Promise<StorageItem> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.put(`/networks/communities/${communityId}/storage/items/${itemId}`, request).then(({ data }) => data);
  },

  /**
   * Move item to a new directory.
   */
  moveItem: async (itemId: number, request: MoveItemRequest): Promise<StorageItem> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.put(`/networks/communities/${communityId}/storage/items/${itemId}/move`, request).then(({ data }) => data);
  },

  /**
   * Move multiple items to a new directory.
   */
  moveBatchItems: async (request: MoveBatchItemsRequest): Promise<{ count: number }> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.put(`/networks/communities/${communityId}/storage/items/batch/move`, request).then(({ data }) => data);
  },

  /**
   * Fetch item file content inline (bypasses CORS on blob storage).
   */
  getItemContent: async (itemId: number): Promise<string> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService
      .get(`/networks/communities/${communityId}/storage/items/${itemId}/content`, { responseType: 'text' })
      .then(({ data }) => data as string);
  },

  /**
   * Delete a single item.
   */
  deleteItem: async (itemId: number): Promise<void> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.delete(`/networks/communities/${communityId}/storage/items/${itemId}`);
  },

  /**
   * Delete multiple items.
   */
  deleteBatchItems: async (request: DeleteBatchItemsRequest): Promise<{ count: number }> => {
    const communityId = store.getState().application.selectedCommunity;
    return httpService.delete(`/networks/communities/${communityId}/storage/items`, { data: request }).then(({ data }) => data);
  },
};

export default storageService;
